// Searchable single-select for country — shows ISO code + name, searches by either
// Uses fixed-position dropdown to escape overflow:hidden parents (same pattern as Select from @startus/ui)
// Keyboard/ARIA (combobox + listbox) comes from useListboxNavigation.
import { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useListboxNavigation } from '@startus/ui';

export interface CountryOption {
  value: string; // ISO2 code
  label: string; // Country name
}

interface CountrySelectProps {
  value: string;
  onChange: (iso: string) => void;
  options: CountryOption[];
  placeholder?: string;
  className?: string;
}

export function CountrySelect({
  value,
  onChange,
  options,
  placeholder,
  className = '',
}: CountrySelectProps) {
  const { t } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<CSSProperties>({});
  const [filtered, setFiltered] = useState<CountryOption[]>(options);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const selected = options.find((o) => o.value === value);

  const applyFilter = useCallback(
    (query: string) => {
      const q = query.toLowerCase();
      setFiltered(
        !q
          ? options
          : options.filter(
              (o) => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q)
            )
      );
    },
    [options]
  );

  const updatePosition = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setDropdownStyle({
      top: rect.bottom + 4,
      right: window.innerWidth - rect.right,
      minWidth: rect.width,
    });
  }, []);

  // Set position before paint to avoid flicker
  useLayoutEffect(() => {
    if (!isOpen) return;
    updatePosition();
  }, [isOpen, updatePosition]);

  // Follow trigger during scroll — capture phase catches nested scrollable containers
  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener('scroll', updatePosition, true);
    return () => window.removeEventListener('scroll', updatePosition, true);
  }, [isOpen, updatePosition]);

  // Focus search on open, reset on close
  useEffect(() => {
    if (!isOpen) {
      if (searchRef.current) searchRef.current.value = '';
      setFiltered(options);
      return;
    }
    applyFilter('');
    setTimeout(() => searchRef.current?.focus(), 0);
  }, [isOpen, applyFilter, options]);

  // Close on outside click
  useEffect(() => {
    const handleOutside = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!containerRef.current?.contains(t) && !dropdownRef.current?.contains(t)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  // The search input unmounts on close — return focus to the trigger.
  const close = () => {
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  const handleSelect = (iso: string) => {
    onChange(iso);
    close();
  };

  const listbox = useListboxNavigation({
    items: filtered,
    isOpen,
    onSelect: (o) => handleSelect(o.value),
    onClose: close,
    selectedIndex: filtered.findIndex((o) => o.value === value),
  });

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    applyFilter(e.target.value);
    listbox.resetActive();
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Trigger — value in blue, no chevron to avoid overflow in constrained rows */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className="text-sm focus:outline-none cursor-pointer max-w-full"
      >
        {selected ? (
          <span className="text-[#19A2F8] block truncate">{selected.label}</span>
        ) : (
          <span className="text-[#EAEAEA]/40 block truncate">
            {placeholder ?? t('common.select')}
          </span>
        )}
      </button>

      {/* Fixed-position dropdown */}
      {isOpen && (
        <div
          ref={dropdownRef}
          style={dropdownStyle}
          className="fixed z-9999 bg-base-100 border border-base-300 rounded-lg shadow-lg flex flex-col min-w-max max-w-75"
        >
          <div className="p-2 border-b border-base-200">
            <input
              ref={searchRef}
              type="text"
              defaultValue=""
              {...listbox.getComboboxProps()}
              onChange={handleSearchChange}
              onMouseDown={(e) => e.stopPropagation()}
              placeholder={t('dropdown.searchCountryPlaceholder')}
              className="input input-sm input-bordered w-full"
            />
          </div>
          {filtered.length === 0 && (
            <p className="px-4 py-2 text-sm text-base-content/40 cursor-default select-none">
              {t('common.noResults')}
            </p>
          )}
          <ul {...listbox.getListboxProps()} className="max-h-52 overflow-y-auto">
            {filtered.map((o, index) => (
              <li
                key={o.value}
                {...listbox.getOptionProps(index, value === o.value)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(o.value)}
                className={`px-4 py-2 text-sm cursor-pointer hover:bg-base-200 flex items-center gap-2 whitespace-nowrap ${listbox.isActive(index) ? 'bg-base-200' : ''} ${value === o.value ? 'bg-primary/10 font-medium' : ''}`}
              >
                <span className="text-base-content/40 font-mono text-sm w-7 shrink-0">
                  {o.value}
                </span>
                <span>{o.label}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
